// src/features/system/devices/components/iot/DspControlPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Collapsible DSP control panel for the IoT Telemetry page.
//
//   • useDSP gateway toggle  (default: ON)
//   • 4 sliders: Glitch Gate · Smoothing · Interpolation · Dead-zone
//   • Save preset to localStorage, Reset to defaults
//
// PAC Glass design system — pac-glass surface, emerald focus rings,
// border-hover emphasis, no solid bg panels.
// ────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useState } from 'react';
import { type DspPreset, DSP_DEFAULTS } from '@/shared/dsp/telemetryDsp';

/* ── localStorage key ─────────────────────────────────────────────────── */
const LS_KEY_DSP_PRESET = 'pac:iot:dsp:preset';
const LS_KEY_DSP_ENABLED = 'pac:iot:dsp:enabled';

/* ── Slider descriptor ────────────────────────────────────────────────── */
interface SliderDef {
  key:   keyof DspPreset;
  label: string;
  hint:  string;
  min:   number;
  max:   number;
  step:  number;
  fmt:   (v: number) => string;
}

const SLIDERS: SliderDef[] = [
  {
    key:   'glitchRadius',
    label: 'Glitch Gate',
    hint:  'Median filter radius — removes spikes. 0 = off, 3 = aggressive.',
    min: 0, max: 3, step: 1,
    fmt: v => v === 0 ? 'off' : `${2 * v + 1}-tap`,
  },
  {
    key:   'smoothingAlpha',
    label: 'Smoothing',
    hint:  'Time-weighted EMA — adapts to sample gaps. 0 = raw, 0.4 = heavy smooth.',
    min: 0, max: 0.5, step: 0.01,
    fmt: v => v === 0 ? 'off' : `α ${v.toFixed(2)}`,
  },
  {
    key:   'interpFactor',
    label: 'Resolution',
    hint:  'Monotone cubic upsample — fills gaps without overshoot. 1 = off.',
    min: 1, max: 8, step: 1,
    fmt: v => v === 1 ? 'off' : `×${v}`,
  },
  {
    key:   'deadZone',
    label: 'Dead-zone',
    hint:  'Suppresses sub-threshold jitter. 0 = off.',
    min: 0, max: 2, step: 0.05,
    fmt: v => v === 0 ? 'off' : `±${v.toFixed(2)}`,
  },
];

/* ── Helpers ──────────────────────────────────────────────────────────── */

function loadPreset(): DspPreset {
  try {
    const raw = localStorage.getItem(LS_KEY_DSP_PRESET);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<DspPreset>;
      return { ...DSP_DEFAULTS, ...parsed };
    }
  } catch { /* ignore corrupt data */ }
  return { ...DSP_DEFAULTS };
}

function loadEnabled(): boolean {
  try {
    const raw = localStorage.getItem(LS_KEY_DSP_ENABLED);
    if (raw !== null) return raw === 'true';
  } catch { /* ignore */ }
  return true; // default ON
}

/* ── Component ────────────────────────────────────────────────────────── */

interface DspControlPanelProps {
  preset:     DspPreset;
  enabled:    boolean;
  onChange:   (p: DspPreset) => void;
  onToggle:   (on: boolean) => void;
}

export default function DspControlPanel({
  preset,
  enabled,
  onChange,
  onToggle,
}: DspControlPanelProps) {
  const [expanded, setExpanded] = useState(false);

  /* Persist to localStorage on every change */
  const persist = useCallback((p: DspPreset) => {
    try { localStorage.setItem(LS_KEY_DSP_PRESET, JSON.stringify(p)); } catch { /* quota */ }
  }, []);

  const persistEnabled = useCallback((on: boolean) => {
    try { localStorage.setItem(LS_KEY_DSP_ENABLED, String(on)); } catch { /* quota */ }
  }, []);

  /* Slider change handler */
  const handleSlider = useCallback((key: keyof DspPreset, val: number) => {
    const next = { ...preset, [key]: val };
    onChange(next);
    persist(next);
  }, [preset, onChange, persist]);

  /* Toggle gateway */
  const handleToggle = useCallback(() => {
    const next = !enabled;
    onToggle(next);
    persistEnabled(next);
  }, [enabled, onToggle, persistEnabled]);

  /* Reset to defaults */
  const handleReset = useCallback(() => {
    const defaults = { ...DSP_DEFAULTS };
    onChange(defaults);
    persist(defaults);
  }, [onChange, persist]);

  /* Save = persist current (already persisted on each change — this is
     explicit "confirm" UX that flashes feedback). */
  const [saved, setSaved] = useState(false);
  const handleSave = useCallback(() => {
    persist(preset);
    setSaved(true);
    setTimeout(() => setSaved(false), 1200);
  }, [preset, persist]);

  return (
    <div className="shrink-0">
      {/* ── Header row: toggle + collapse trigger ──────────────────── */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-md
                   pac-glass-subtle border border-white/[0.06]
                   hover:border-white/[0.12] transition-colors group"
      >
        {/* DSP toggle switch */}
        <span
          role="switch"
          aria-checked={enabled}
          tabIndex={0}
          onClick={e => { e.stopPropagation(); handleToggle(); }}
          onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleToggle(); } }}
          className={`relative inline-flex items-center h-5 w-9 shrink-0 rounded-full
                      border transition-colors cursor-pointer
                      focus:outline-none focus:ring-1 focus:ring-emerald-500
                      ${enabled
                        ? 'bg-emerald-600/40 border-emerald-500/60'
                        : 'bg-white/[0.04] border-white/[0.10]'}`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 rounded-full transition-transform
                        ${enabled
                          ? 'translate-x-[18px] bg-emerald-400'
                          : 'translate-x-[2px] bg-neutral-500'}`}
          />
        </span>

        <span className="text-xs font-semibold tracking-wide uppercase text-neutral-300">
          DSP
        </span>

        {/* Expand / collapse chevron */}
        <svg
          className={`ml-auto w-3.5 h-3.5 text-neutral-500 transition-transform duration-200
                      ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* ── Expanded slider panel ──────────────────────────────────── */}
      {expanded && (
        <div className="mt-1 px-3 py-3 rounded-md pac-glass border border-white/[0.06]
                        flex flex-col gap-4">
          {SLIDERS.map(s => {
            const val = preset[s.key];
            return (
              <div key={s.key} className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-medium tracking-wide uppercase text-neutral-400">
                    {s.label}
                  </label>
                  <span className="text-[11px] font-mono text-emerald-400 tabular-nums">
                    {s.fmt(val)}
                  </span>
                </div>

                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  value={val}
                  disabled={!enabled}
                  onChange={e => handleSlider(s.key, parseFloat(e.target.value))}
                  title={s.hint}
                  className="w-full h-1 appearance-none rounded-full cursor-pointer
                             bg-white/[0.06]
                             accent-emerald-500
                             disabled:opacity-30 disabled:cursor-not-allowed
                             [&::-webkit-slider-thumb]:appearance-none
                             [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3
                             [&::-webkit-slider-thumb]:rounded-full
                             [&::-webkit-slider-thumb]:bg-emerald-400
                             [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-emerald-600
                             [&::-webkit-slider-thumb]:shadow-[0_0_6px_rgba(16,185,129,0.5)]
                             [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3
                             [&::-moz-range-thumb]:rounded-full
                             [&::-moz-range-thumb]:bg-emerald-400
                             [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-emerald-600"
                />

                <span className="text-[9px] text-neutral-600 leading-tight">
                  {s.hint}
                </span>
              </div>
            );
          })}

          {/* ── Action row ──────────────────────────────────────────── */}
          <div className="flex items-center gap-2 pt-1 border-t border-white/[0.04]">
            <button
              onClick={handleSave}
              className="pac-glass-btn px-3 py-1.5 rounded-md text-[11px] font-semibold
                         text-emerald-400 hover:border-emerald-500 tracking-wide uppercase"
            >
              {saved ? '✓ Saved' : 'Save preset'}
            </button>
            <button
              onClick={handleReset}
              className="pac-glass-btn px-3 py-1.5 rounded-md text-[11px] font-semibold
                         text-neutral-500 hover:text-neutral-300 hover:border-neutral-400
                         tracking-wide uppercase"
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Hook: load persisted DSP state ───────────────────────────────────── */

export function useDspPreset(): {
  preset:   DspPreset;
  enabled:  boolean;
  setPreset: React.Dispatch<React.SetStateAction<DspPreset>>;
  setEnabled: React.Dispatch<React.SetStateAction<boolean>>;
} {
  const [preset,  setPreset]  = useState<DspPreset>(loadPreset);
  const [enabled, setEnabled] = useState<boolean>(loadEnabled);
  return { preset, enabled, setPreset, setEnabled };
}
